import { db } from "../firebase";
import {
  doc,
  getDoc,
  updateDoc,
  addDoc,
  collection,
  serverTimestamp,
} from "firebase/firestore";

// Heurísticas para detectar intentos de compartir información de contacto
// (para coordinar trabajo fuera de la plataforma). No es infalible: corre en
// el cliente y puede evadirse, pero cubre los casos más comunes.
const EMAIL_RE = /[a-z0-9._%+-]+\s*(@|\(at\)|\bat\b|arroba)\s*[a-z0-9.-]+\s*(\.|\(dot\)|\bdot\b|punto)\s*[a-z]{2,}/i;
const URL_RE = /https?:\/\/\S+|www\.\S+|\b[a-z0-9-]+\.(com|net|org|io|co|ar|me|xyz|link)\b/i;
const PHONE_RE = /(?:\+?\d[\s.-]?){8,}/;
const NUMBER_WORD = "(cero|uno|dos|tres|cuatro|cinco|seis|siete|ocho|nueve)";
const SPELLED_PHONE_RE = new RegExp(`\\b${NUMBER_WORD}\\b(?:\\s+\\b${NUMBER_WORD}\\b){5,}`, "i");
const CONTACT_APP_RE = /\b(whatsapp|wsp|whats ?app|telegram|instagram|\big\b|facebook|\bfb\b|skype|discord|linkedin|snapchat|tiktok)\b/i;
const HANDLE_RE = /(^|\s)@[a-z0-9_.]{3,}/i;

// Frases que delatan la intención de coordinar por fuera de la plataforma,
// aunque el mensaje no incluya ningún dato de contacto en sí. Agrupadas por tema
// para que sea fácil sumar más variantes a futuro.
const INTENT_PHRASES = [
  // Mencionar "fuera de la plataforma" directamente
  /fuera de (la |esta )?plataforma/i,
  /por fuera de (skach|la app)/i,
  /salgamos de (la plataforma|skach|la app)/i,
  /sin pasar por (la )?(plataforma|skach|la app)/i,
  /no (hace falta|necesitamos|necesito) (usar|pasar por) (la )?(plataforma|skach|la app)/i,

  // Evitar la comisión / el pago por la plataforma
  /evit\w* (la |el )?(comisi[oó]n|porcentaje)/i,
  /ahorr\w* (la |el )?(comisi[oó]n|porcentaje)/i,
  /sin (pagar |la )?comisi[oó]n/i,
  /sin intermediarios/i,

  // Pedir o dar directamente un dato de contacto
  /te paso mi (n[uú]mero|contacto|whatsapp|mail|correo|email)/i,
  /pasame tu (n[uú]mero|contacto|whatsapp|mail|correo|email)/i,
  /mi (n[uú]mero|whatsapp|mail|correo|email) es/i,
  /te dejo mi (contacto|n[uú]mero|whatsapp|mail|correo|email)/i,
  /dame tu (contacto|n[uú]mero|whatsapp|mail|correo|email)/i,
  /dejame tu (contacto|n[uú]mero|whatsapp|mail|correo|email)/i,
  /busco tu (n[uú]mero|contacto|mail|correo|email)/i,
  /agregame (a|al|en) (whatsapp|instagram|telegram)/i,
  /agregame (de |por )?contacto/i,

  // Proponer cambiar de canal de comunicación
  /hablemos por (whatsapp|telegram|instagram|fuera)/i,
  /nos comunicamos por fuera/i,
  /seguimos (por privado|por fuera|hablando por fuera)/i,
  /escrib[ií]me directo/i,
  /mand(a|á)me (un )?mensaje directo/i,
  /(hablar|hablemos|coordinar|coordinemos|comunicarnos|comuniquemos) (directo|por fuera|aparte|afuera)/i,
  /vamos a otro medio/i,
  /cambiemos de plataforma/i,
  /mejor nos vemos en persona/i,

  // Cerrar el trabajo/pago por fuera del flujo de la plataforma
  /trabajemos aparte/i,
  /contact(a|á)(me|rme)? (directo|afuera)/i,
  /coordinamos por fuera/i,
  /cerremos (el trato|esto) aparte/i,
  /arregl(amos|emos) (por fuera|aparte)/i,
  /pago aparte/i,
  /cobro aparte/i,
];

const INTENT_PHRASE_RE = { test: (text) => INTENT_PHRASES.some((re) => re.test(text)) };

const CHECKS = [
  { reason: "email", re: EMAIL_RE },
  { reason: "url", re: URL_RE },
  { reason: "telefono", re: PHONE_RE },
  { reason: "telefono_deletreado", re: SPELLED_PHONE_RE },
  { reason: "app_de_contacto", re: CONTACT_APP_RE },
  { reason: "usuario_red_social", re: HANDLE_RE },
  { reason: "intencion_explicita", re: INTENT_PHRASE_RE },
];

// Detecta intentos de esconder un teléfono o email intercalando letras al azar
// entre los caracteres (ej: "023kjhdsf23mnsd68ksandf07mnscs87akjdak" contiene
// "02323680787" si le sacás las letras). Se analiza palabra por palabra
// (separadas por espacios), no la frase completa, para no disparar con texto normal.
const NOISE_TOKEN_MIN_LENGTH = 18;
const NOISE_TOKEN_MIN_DIGITS = 6;
const NOISE_TOKEN_MIN_LETTERS = 4;
const OBFUSCATED_PHONE_MIN_DIGITS = 8;

const detectObfuscatedTokens = (text) => {
  const tokens = text.split(/\s+/).filter(Boolean);
  let obfuscatedPhone = false;
  let noiseToken = false;

  for (const token of tokens) {
    const digitCount = (token.match(/\d/g) || []).length;
    const letterCount = (token.match(/[a-zà-ÿ]/gi) || []).length;

    // Sacando las letras, quedan 8+ dígitos seguidos: parece un teléfono disfrazado.
    if (digitCount >= OBFUSCATED_PHONE_MIN_DIGITS) {
      obfuscatedPhone = true;
    }

    // Palabra larga y sin sentido que mezcla números y letras: no es una palabra
    // real, parece un teléfono/email/usuario codificado a mano.
    if (
      token.length >= NOISE_TOKEN_MIN_LENGTH &&
      digitCount >= NOISE_TOKEN_MIN_DIGITS &&
      letterCount >= NOISE_TOKEN_MIN_LETTERS
    ) {
      noiseToken = true;
    }
  }

  return { obfuscatedPhone, noiseToken };
};

export const detectContactInfoAttempt = (text) => {
  if (!text || !text.trim()) {
    return { flagged: false, reasons: [] };
  }
  const reasons = CHECKS.filter(({ re }) => re.test(text)).map(({ reason }) => reason);

  const { obfuscatedPhone, noiseToken } = detectObfuscatedTokens(text);
  if (obfuscatedPhone) reasons.push("telefono_ofuscado");
  if (noiseToken) reasons.push("token_sospechoso");

  return { flagged: reasons.length > 0, reasons };
};

// Aplica un "strike" de moderación al usuario y devuelve el nuevo estado de la cuenta.
// 1er strike -> cuenta suspendida. 2do strike en adelante -> marcada para revisión/borrado.
export const applyModerationStrike = async ({ uid, chatPath, message, reasons }) => {
  const userRef = doc(db, "users", uid);
  const userSnap = await getDoc(userRef);
  const currentStrikes = userSnap.exists() ? userSnap.data().contactInfoStrikes || 0 : 0;
  const strikes = currentStrikes + 1;
  const accountStatus = strikes >= 2 ? "pending_deletion" : "suspended";

  await updateDoc(userRef, {
    contactInfoStrikes: strikes,
    accountStatus,
    accountStatusUpdatedAt: serverTimestamp(),
  });

  await addDoc(collection(db, "moderationLogs"), {
    uid,
    chatPath,
    message,
    reasons,
    strikeNumber: strikes,
    resultingStatus: accountStatus,
    timestamp: serverTimestamp(),
  });

  return { strikes, accountStatus };
};

export const ACCOUNT_STATUS_COPY = {
  suspended: {
    title: "Tu cuenta fue suspendida",
    body: "Detectamos un intento de compartir información de contacto para coordinar trabajo fuera de la plataforma, lo cual va contra nuestras reglas. Tu cuenta quedó suspendida mientras se revisa el caso.",
  },
  pending_deletion: {
    title: "Tu cuenta está en revisión para ser eliminada",
    body: "Volviste a intentar compartir información de contacto después de una advertencia previa. Tu cuenta fue marcada para revisión y puede ser eliminada definitivamente.",
  },
};
